import { useEffect, useState } from "react";
import { FileText, ExternalLink, Database, Quote } from "lucide-react";
import DOMPurify from "dompurify";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { fetchSourceDoc } from "../../lib/api";
import { textFragment, sourceDeepLink, orderRows } from "../../lib/sourceLinks";

export default function SourceDialog({ c, src, onClose }) {
  const dim = src ? c[src.dim] : null;
  const [doc, setDoc] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  useEffect(() => {
    setDoc(null);
    if (src && dim && !dim.absent) {
      setDocLoading(true);
      fetchSourceDoc(c.ticker, src.dim).then(setDoc).catch(() => setDoc(null)).finally(() => setDocLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src && src.dim, c.ticker]);
  if (!src) return null;
  const isXbrl = dim.source === "xbrl";
  const isFiling = doc?.kind === "filing";
  const synthetic = /^(Audited:|Per XBRL)/.test(dim.raw_sentence || "");
  // When we have a located filing snippet, open OUR document view — it reliably scrolls to and
  // highlights the passage (scroll-to-text URL fragments are flaky on multi-MB SEC filings).
  const url = isFiling
    ? `${process.env.REACT_APP_BACKEND_URL}/api/company/${c.ticker}/filing-doc/${src.dim}`
    : sourceDeepLink(c, dim);
  return (
    <Dialog open={!!src} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="source-dialog" className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[#1a1a18]">{src.label} — source</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span data-testid="source-type-badge"
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${isXbrl ? "bg-[#E6EEF8] text-[#1f4e79]" : "bg-[#EDE7F6] text-[#5b3f8a]"}`}>
              {isXbrl ? <Database className="h-3.5 w-3.5" /> : <Quote className="h-3.5 w-3.5" />}
              {isXbrl ? "Structured XBRL financial data" : "Extracted from filing text"}
            </span>
            <span className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-medium text-neutral-600">{dim.form_type}</span>
            {dim.filing_date && <span className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-medium text-neutral-600">Filed {dim.filing_date}</span>}
          </div>

          {!dim.absent && (
            <div className="rounded-md border border-[#e8e6df] bg-[#f5f4ef] p-4">
              <div className="text-xs uppercase tracking-wider text-neutral-400">Extracted figure</div>
              <div className="mt-1 font-heading text-3xl font-bold text-[#1a1a18]">
                {dim.level && !dim.top_pct ? dim.label : `${dim.top_pct}%`}
              </div>
              <div className="text-sm text-neutral-600">{dim.top_entity}</div>
              {dim.basis && (
                <div data-testid="source-basis-note" className="mt-2 text-xs text-neutral-500">
                  Measured against <span className="font-semibold">{dim.basis}</span>
                  {dim.basis_share
                    ? <> — which the filing states are <span className="font-semibold">{dim.basis_share}% of total revenues</span>, so ≈<span className="font-semibold">{Math.round(dim.top_pct * dim.basis_share / 100)}%</span> of the company's total.</>
                    : <> — the basis stated in the filing, not necessarily total revenue.</>}
                </div>
              )}
            </div>
          )}

          {(docLoading || doc?.html) && (
            <div>
              <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">
                <FileText className="h-3.5 w-3.5" />
                {docLoading ? "Loading the filing…" : isFiling
                  ? `The disclosure as it appears in the ${dim.form_type || "10-K"}${doc.section ? ` — from ${doc.section}` : ""}`
                  : "The disclosure table — SEC EDGAR rendering"}
              </div>
              {docLoading ? (
                <div className="flex h-32 items-center justify-center rounded-md border border-[#e8e6df] bg-white text-sm text-neutral-400">
                  Loading the SEC document…
                </div>
              ) : (
                <div data-testid="sec-doc-view"
                  className={`${isFiling ? "sec-filing" : "sec-doc"} max-h-96 overflow-auto rounded-md border border-[#e8e6df] bg-white p-4 shadow-sm`}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(doc.html) }} />
              )}
              {!docLoading && (
                <p className="mt-1 text-[11px] text-neutral-400">
                  {isFiling
                    ? "Reproduced verbatim from the filed document on SEC EDGAR — original formatting preserved."
                    : "SEC EDGAR's rendering of the tagged financial data for this disclosure."}
                </p>
              )}
              {dim.basis_note && !docLoading && !isFiling && (
                <p data-testid="source-basis-note" className="mt-2 rounded-md border border-[#E0922F]/40 bg-[#FBF3E6] px-3 py-2 text-xs italic leading-relaxed text-[#8a5a12]">
                  Filing footnote: "{dim.basis_note}"
                </p>
              )}
            </div>
          )}

          <div>
            <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">
              {isXbrl ? "As tagged in the filing's XBRL" : synthetic ? "Standardized figure (audit-verified)" : "Exact language in the filing"}
            </div>
            {src.dim === "product" && Array.isArray(dim.products) && dim.products.length > 1 ? (
              <div className="space-y-3" data-testid="source-quote">
                {orderRows(dim.products).map((p) => (
                  <div key={`${p.name}-${p.pct}`} className="rounded-md border border-[#e8e6df] bg-white p-4">
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-neutral-600">{p.name}</span>
                      <span className="font-mono text-xs font-bold text-[#1a1a18]">{p.pct}%</span>
                    </div>
                    <p className="font-mono text-sm leading-relaxed text-neutral-700">"{p.raw_sentence}"</p>
                    {p.source_url && (
                      <a href={p.source_url + textFragment(p.raw_sentence)} target="_blank" rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#8a6d2f] hover:underline">
                        Open in filing <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p data-testid="source-quote" className="rounded-md border border-[#e8e6df] bg-white p-4 font-mono text-sm leading-relaxed text-neutral-700">
                "{dim.raw_sentence}"
              </p>
            )}
          </div>

          <div className="rounded-md bg-[#f5f4ef] p-3 font-mono text-xs text-neutral-500">
            CIK {c.cik} · Accession {dim.accession_number || "—"}
          </div>

          {url ? (
            <a href={url} target="_blank" rel="noopener noreferrer" data-testid="sec-edgar-link"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1a1a18] px-5 py-3 text-sm font-medium text-white transition-transform active:scale-95 hover:opacity-90">
              {isFiling ? "Open the filing at this disclosure" : isXbrl ? "Open the exact table on SEC EDGAR" : "Open the filing at this disclosure"} <ExternalLink className="h-4 w-4" />
            </a>
          ) : (
            <p className="text-center text-sm text-neutral-400">No source filing recorded for this dimension yet.</p>
          )}
          <p className="text-center text-[11px] text-neutral-400">
            {isFiling
              ? "Opens the filed 10-K (verbatim from SEC EDGAR) scrolled to this disclosure, with a link to the original."
              : isXbrl
              ? "Opens the official SEC EDGAR financial-statement table this figure was tagged in."
              : "Opens the original 10-K on SEC EDGAR and scrolls to / highlights this exact sentence (Chrome, Edge & Safari)."}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
